<template>
  <v-card class="rj">
    <!-- El rojo se reserva para la acción destructiva: aquí es sólo una regla
         lateral. Mientras se llena el formulario nada grita; el peso visual
         llega al final, en el botón que ejecuta el rechazo. -->
    <header class="rj__head">
      <div class="rj__head-text">
        <p class="rj__eyebrow">Trámites en proceso</p>
        <h2 class="rj__title">Rechazar trámite</h2>
        <p class="rj__scope">
          <template v-if="isGroup">
            Consolidado <strong>{{ code }}</strong> ·
            {{ invoices.length }} facturas
          </template>
          <template v-else>
            Factura <strong>{{ item.invoiceNumber }}</strong>
          </template>
        </p>
      </div>
      <button
        type="button"
        class="rj__close"
        title="Cerrar"
        @click="$emit('close-rejected')"
      >
        <v-icon small>close</v-icon>
      </button>
    </header>

    <div class="rj__body">
      <p v-if="isGroup" class="rj__note">
        Se rechazan las {{ invoices.length }} facturas del consolidado:
        {{ invoices.map(i => i.invoiceNumber).join(', ') }}
      </p>

      <!-- 1. Soporte -->
      <section class="rj__section">
        <p class="rj__label">
          Soporte del rechazo
          <span class="rj__req">obligatorio</span>
        </p>
        <upload-btn
          multiple
          dropzone
          :invalid="submitted && !hasFiles"
          @clicked="onFilePicked"
        ></upload-btn>
        <p v-if="submitted && !hasFiles" class="rj__error">
          Adjunta al menos un documento para poder rechazar.
        </p>
      </section>

      <!-- 2. Motivo -->
      <section class="rj__section">
        <p class="rj__label">Motivo del rechazo</p>
        <textarea
          v-model="form.observations"
          v-validate="'required'"
          class="rj__textarea"
          :class="{ 'rj__textarea--error': submitted && !form.observations }"
          data-vv-name="comment"
          rows="3"
          placeholder="Explica qué debe corregirse."
        ></textarea>
        <p v-if="submitted && !form.observations" class="rj__error">
          Escribe el motivo: es lo que leerá el cliente.
        </p>
        <p v-else class="rj__help">
          Se envía al cliente junto con los documentos adjuntos.
        </p>
      </section>

      <!-- 3. Expediente. El orden cronológico es información real: un trámite
           puede volver a este mismo paso varias veces, y cada vuelta deja su
           soporte. Por eso se lee como línea de tiempo y no como lista. -->
      <section class="rj__section">
        <p class="rj__label">
          Expediente del trámite
          <span class="rj__count">{{ documents.length }}</span>
        </p>
        <p v-if="loadingDocs" class="rj__help">Cargando expediente…</p>
        <p v-else-if="!documents.length" class="rj__help">
          Sin documentos previos. Este será el primero.
        </p>
        <ol v-else class="rj__log">
          <li v-for="doc in documents" :key="doc.id" class="rj__log-item">
            <span class="rj__log-date">{{ dateOf(doc.createdAt) }}</span>
            <span class="rj__tag" :class="stepClass(doc.step)">
              {{ stepLabel(doc.step) }}
            </span>
            <a
              :href="fullPath(doc.filePath)"
              target="_blank"
              rel="noopener"
              class="rj__log-name"
              :title="doc.originalName || doc.filePath"
            >
              {{ doc.originalName || doc.filePath }}
            </a>
            <span v-if="doc.uploadedBy" class="rj__log-who">
              {{ doc.uploadedBy.firstName }} {{ doc.uploadedBy.lastName }}
            </span>
          </li>
        </ol>
      </section>
    </div>

    <footer class="rj__foot">
      <button
        type="button"
        class="rj__btn rj__btn--ghost"
        @click="$emit('close-rejected')"
      >
        Cancelar
      </button>
      <button
        type="button"
        class="rj__btn rj__btn--reject"
        :disabled="saving"
        @click="handleSave"
      >
        <v-icon small class="rj__btn-icon">block</v-icon>
        {{ saving ? 'Rechazando…' : rejectLabel }}
      </button>
    </footer>
  </v-card>
</template>

<script>
import moment from 'moment-timezone'
import UploadButton from './FileUpload'
import { getFullPath } from '@/helpers/TransitHelper'

export default {
  components: {
    'upload-btn': UploadButton,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    // Facturas del consolidado cuando el rechazo es de todo el grupo. Si no
    // llega, se rechaza sólo `item` y el comportamiento es el de siempre.
    invoices: {
      type: Array,
      default: null,
    },
    code: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    form: {
      invoiceId: '',
      observations: '',
      files: null,
    },
    documents: [],
    loadingDocs: false,
    // Los errores aparecen al intentar guardar, no al abrir: el formulario no
    // debe reclamar antes de que el usuario haya hecho nada.
    submitted: false,
    saving: false,
  }),
  computed: {
    isGroup() {
      return Array.isArray(this.invoices) && this.invoices.length > 1
    },
    hasFiles() {
      return Array.isArray(this.form.files) && this.form.files.length > 0
    },
    // Ids sobre los que aplica el rechazo: la factura, o todo el consolidado.
    targetIds() {
      return this.isGroup ? this.invoices.map(i => i.id) : [this.item.id]
    },
    rejectLabel() {
      return this.isGroup ? 'Rechazar consolidado' : 'Rechazar trámite'
    },
  },
  created() {
    // Un arreglo viaja como JSON en el multipart (ver createFormData); el
    // backend lo deserializa. Con una sola factura se manda el id pelado.
    this.form.invoiceId = this.isGroup
      ? this.invoices.map(i => i.id)
      : this.item.id
    this.loadDocuments()
  },
  methods: {
    fullPath: getFullPath,
    onFilePicked(files) {
      this.form.files = files
    },
    dateOf(value) {
      return value ? moment(value).format('DD/MM/YYYY') : '—'
    },
    stepLabel(step) {
      const labels = {
        DENIED: 'Rechazo',
        PAYED: 'Pago',
        DELIVERED: 'Entrega',
        OTHER: 'Otro',
      }
      return labels[step] || step
    },
    stepClass(step) {
      return `rj__tag--${String(step).toLowerCase()}`
    },
    loadDocuments() {
      this.loadingDocs = true
      this.$store
        .dispatch('invoices/getDocuments', { ids: this.targetIds.join(',') })
        .then(docs => {
          this.documents = docs || []
        })
        .finally(() => {
          this.loadingDocs = false
        })
    },
    handleSave() {
      this.submitted = true
      this.$validator.validateAll().then(result => {
        if (!result || !this.hasFiles) return
        this.saving = true
        this.$store
          .dispatch('transit/denied', this.form)
          .then(() => {
            this.$emit('save-rejected')
          })
          .finally(() => {
            this.saving = false
          })
      })
    },
  },
}
</script>

<style scoped>
.rj {
  border-radius: 12px;
  overflow: hidden;
}

/* === Encabezado === */
.rj__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px 24px;
  border-left: 4px solid #b91c1c;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
}
.rj__head-text {
  flex: 1;
  min-width: 0;
}
.rj__eyebrow {
  margin: 0;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}
.rj__title {
  margin: 2px 0 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.2;
  color: #0f172a;
}
.rj__scope {
  margin: 4px 0 0;
  font-size: 13px;
  color: #64748b;
}
.rj__scope strong {
  color: #254f87;
}
.rj__close {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px;
  cursor: pointer;
  line-height: 1;
  transition: background 0.18s ease;
}
.rj__close:hover {
  background: #f1f5f9;
}
.rj__close .v-icon {
  color: #64748b !important;
}

/* === Cuerpo === */
.rj__body {
  padding: 16px 24px 4px;
  max-height: 62vh;
  overflow-y: auto;
}
.rj__note {
  margin: 0 0 14px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  font-size: 12px;
  color: #7f1d1d;
}
.rj__section {
  margin-bottom: 18px;
}
.rj__label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
}
.rj__req {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
  color: #b91c1c;
}
.rj__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
}
.rj__error {
  margin: 6px 0 0;
  font-size: 12px;
  color: #b91c1c;
}
.rj__help {
  margin: 6px 0 0;
  font-size: 12px;
  color: #94a3b8;
}

/* === Motivo === */
.rj__textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  color: #0f172a;
  background: #ffffff;
  resize: vertical;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}
.rj__textarea:focus {
  outline: none;
  border-color: #254f87;
  box-shadow: 0 0 0 3px rgba(37, 79, 135, 0.15);
}
.rj__textarea--error {
  border-color: #fca5a5;
  background: #fef2f2;
}
.rj__textarea::placeholder {
  color: #94a3b8;
}

/* === Expediente ===
   Línea de tiempo: la regla vertical y la fecha al frente muestran que el
   trámite puede volver al mismo paso varias veces. */
.rj__log {
  list-style: none;
  margin: 0;
  padding: 0 0 0 12px;
  border-left: 2px solid #e2e8f0;
  max-height: 150px;
  overflow-y: auto;
}
.rj__log-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  font-size: 12px;
}
.rj__log-date {
  flex: 0 0 auto;
  color: #94a3b8;
  font-variant-numeric: tabular-nums;
}
.rj__tag {
  flex: 0 0 auto;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}
.rj__tag--denied {
  background: #fee2e2;
  color: #991b1b;
}
.rj__tag--payed {
  background: #dbeafe;
  color: #1e40af;
}
.rj__tag--delivered {
  background: #dcfce7;
  color: #166534;
}
.rj__tag--other {
  background: #f1f5f9;
  color: #475569;
}
.rj__log-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #254f87;
  text-decoration: none;
}
.rj__log-name:hover {
  text-decoration: underline;
}
.rj__log-who {
  flex: 0 0 auto;
  color: #94a3b8;
}

/* === Pie === */
.rj__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 24px 18px;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
}
.rj__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: all 0.18s ease;
}
.rj__btn:focus-visible {
  outline: 2px solid #94a3b8;
  outline-offset: 2px;
}
.rj__btn--ghost {
  background: #ffffff;
  border-color: #e2e8f0;
  color: #475569;
}
.rj__btn--ghost:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
}
/* Blanco sobre #b91c1c = 6.47:1, cumple WCAG AA. */
.rj__btn--reject {
  background: #b91c1c;
  border-color: #b91c1c;
  color: #ffffff;
  box-shadow: 0 1px 2px rgba(69, 10, 10, 0.3),
    0 4px 10px rgba(185, 28, 28, 0.25);
}
.rj__btn--reject .rj__btn-icon {
  color: #ffffff !important;
}
.rj__btn--reject:hover:not(:disabled) {
  background: #991b1b;
  border-color: #991b1b;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(69, 10, 10, 0.35),
    0 8px 18px rgba(185, 28, 28, 0.3);
}
.rj__btn--reject:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: inset 0 2px 4px rgba(69, 10, 10, 0.4);
}
.rj__btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  transform: none;
}
</style>
